
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Sun, Navigation, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const CaravanTravelTips = () => {
  const topDestinations = [
    {
      name: "Nuuksion kansallispuisto",
      description: "Upea luontokohde Espoossa, täydellinen matkailuautolomalle.",
      location: "Espoo, Suomi",
    },
    {
      name: "Saimaan alue",
      description: "Järvisuomen helmi, tarjoaa upeita maisemia ja aktiviteetteja.",
      location: "Etelä-Karjala, Suomi",
    },
    {
      name: "Lapin kultareitti",
      description: "Legendaarinen reitti läpi Lapin kauneimpien maisemien.",
      location: "Lappi, Suomi",
    },
  ];

  const seasonalTips = [
    {
      season: "Kesä",
      tips: [
        "Varaa leirintäalueet etukäteen ruuhka-aikoina",
        "Tarkista ilmastoinnin toimivuus",
        "Hanki hyttysverkot ikkunoihin",
      ],
    },
    {
      season: "Talvi",
      tips: [
        "Varmista lämmitysjärjestelmän kunto",
        "Käytä talvirenkaita",
        "Pidä mukana lämpöeristeitä",
      ],
    },
  ];

  return (
    <section className="py-16 relative">
      {/* Background with gradient and pattern */}
      <div className="absolute inset-0 bg-gradient-to-b from-blue-50/80 to-white">
        <div className="absolute inset-0 opacity-5 background-pattern" />
      </div>
      
      <div className="container mx-auto px-4 relative">
        <h2 className="text-3xl md:text-4xl font-playfair font-bold mb-12 text-center bg-gradient-to-r from-blue-700 to-blue-500 bg-clip-text text-transparent">
          Parhaat matkailuvinkit ja kohteet 2025
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Top destinations */}
          <Card className="bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8">
              <div className="flex items-center mb-6">
                <MapPin className="w-6 h-6 text-blue-600 mr-3" />
                <h3 className="text-2xl font-semibold">Suositut matkakohteet</h3>
              </div>
              <div className="space-y-6">
                {topDestinations.map((dest, idx) => (
                  <div key={idx} className="border-b pb-4 last:border-0 last:pb-0">
                    <h4 className="font-medium text-xl mb-2">{dest.name}</h4>
                    <p className="text-gray-700 mb-2">{dest.description}</p>
                    <p className="text-sm text-blue-600 font-medium">{dest.location}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Seasonal tips */}
          <Card className="bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8">
              <div className="flex items-center mb-6">
                <Sun className="w-6 h-6 text-blue-600 mr-3" />
                <h3 className="text-2xl font-semibold">Kausivinkit</h3>
              </div>
              <div className="space-y-8">
                {seasonalTips.map((season, idx) => (
                  <div key={idx}>
                    <h4 className="font-medium text-xl mb-4">{season.season}</h4>
                    <ul className="space-y-3">
                      {season.tips.map((tip, tipIdx) => (
                        <li key={tipIdx} className="flex items-start">
                          <ShieldCheck className="w-5 h-5 text-blue-500 mt-1 mr-3 flex-shrink-0" />
                          <span className="text-gray-700 text-lg">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* European destinations */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { country: "Norja", highlight: "Vuonoreitit ja Geirangervuono", image: "/lovable-uploads/b78771bc-6f2b-4627-930c-61935571bf2d.png" },
            { country: "Ruotsi", highlight: "Tukholman saaristo", image: "/lovable-uploads/62babc88-6891-4588-b100-ec8a342d8586.png" },
            { country: "Saksa", highlight: "Romantische Straße", image: "/lovable-uploads/e03d0d36-e720-45f5-966f-cf012eeffe74.png" }
          ].map((dest, idx) => (
            <div key={idx} className="relative group overflow-hidden rounded-2xl aspect-[4/3]">
              <img
                src={dest.image}
                alt={dest.country}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6 flex flex-col justify-end">
                <h3 className="text-2xl font-bold text-white mb-2">{dest.country}</h3>
                <p className="text-white/90 text-lg">{dest.highlight}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaravanTravelTips;
